// ── "Notify me on this device": the browser's own banners for chat (report d5b1d1de) ────────────────────────────────
// Calem, asked whether a chime on an open screen would do: "A real notification that arrives even when the app is
// closed. The phone or computer rings and shows a banner like WhatsApp does. Yes each person must allow push
// notifications on their device."
// The cards at the bottom left (components/Chat/ChatToast.tsx) are drawn by a page, so they only ever reach somebody
// with the system open in front of them. A banner on a shut browser is the browser's own doing: it is delivered to
// the push service that browser belongs to and drawn by the service worker the chat already registers (public/sw.js),
// which the browser wakes for it. This file is the page's side of the arrangement:
// · ask the person — on a BUTTON PRESS and never on load, because a browser that is asked out of nowhere refuses for
//   good and there is then no way back but the site settings;
// · subscribe this device with the server's public key and hand the subscription over (server routes/chat.ts /push);
// · say, while a chat is open, that this device is looking at it — the one thing that keeps a banner off the screen
//   the message is already arriving on;
// · and stop again, which leaves the in-app cards exactly as they are.
// Each device is its own answer: allowing it on the office computer says nothing about the phone, which is what the
// reporter asked for ("each person must allow push notifications on their device").
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { runningAsApp } from './installApp';

const SW_PATH = '/sw.js';
const SW_SCOPE = '/chat';        // the worker's own scope, as components/Chat/ChatApp.tsx registers it
const ACTIVE_WAIT = 4_000;       // how long a press waits for a freshly installed worker before going ahead anyway
const HERE_MS = 20_000;          // how often an open chat window says "this device is looking at it"
// The phone's "Turn on notifications" banner, once put away with its ×, stays away on this device (report ec0c5a76).
// Kept in the browser, as the subscription itself is: it is this device's answer, not the person's.
const ASK_KEY = 'mf_chatPushAsk';

/** Can this browser receive a banner at all? An old browser, a page served over plain http:// and Safari on an
 *  iPhone that has not been added to the Home Screen all answer no, and no button is offered — the iPhone is told
 *  how to get one instead (pushNeedsHomeScreen, below). */
export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator
    && 'PushManager' in window && 'Notification' in window;
}

/**
 * An iPhone browser tab, where banners become possible only once Chat is on the Home Screen (report ec0c5a76,
 * opening #2: "Allow pop-up notifications on the user's phone for the chat app"). An iPhone hands a page the means to
 * ask for them — PushManager, Notification — only when the page was added to the Home Screen and is opened from its
 * icon (iOS 16.4 and later); in a tab neither exists, so pushSupported() is false and the button used to vanish
 * without a word. This is what lets the chat say what to tap instead.
 * Read off what the browser calls itself, as nothing else tells an iPhone tab from an old browser: "iPhone" (an iPad
 * calls itself a Mac, and is not phone-shaped anyway), and "Safari/", which Safari and the iPhone's other browsers
 * carry — every one of them has Share → Add to Home Screen — while an app's own built-in browser (LINE, Facebook)
 * does not, and has no such button to tap. An iPhone older than 16.4 gets no banners even from the Home Screen, so
 * it is told nothing rather than something untrue.
 */
export function pushNeedsHomeScreen(): boolean {
  if (typeof window === 'undefined' || pushSupported() || runningAsApp()) return false;
  const ua = navigator.userAgent || '';
  if (!/iPhone|iPod/.test(ua) || !/Safari\//.test(ua)) return false;
  const v = /OS (\d+)_(\d+)/.exec(ua);
  return !v || Number(v[1]) > 16 || (Number(v[1]) === 16 && Number(v[2]) >= 4);
}

function askPutAway(): boolean {
  try { return localStorage.getItem(ASK_KEY) === 'off'; } catch { return false; }
}

/** The server's public key, which arrives as base64url text, as the bytes the subscribe call wants. */
function keyBytes(key: string): Uint8Array {
  const pad = '='.repeat((4 - (key.length % 4)) % 4);
  const raw = window.atob((key + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * The chat worker, registered if it is not yet, with an active worker if one can be waited for.
 * Registered HERE as well as in the app, so the button works in the pop-up over the Dashboard too: a page may
 * register a worker for a narrower address than its own, and a subscription made on that registration is the
 * device's whichever page made it.
 * ⚠️ Not `navigator.serviceWorker.ready` — that waits for the worker controlling THIS page, and on any page but
 * /chat nothing ever will, so the press would hang for good.
 */
async function chatWorker(): Promise<ServiceWorkerRegistration | null> {
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
    if (reg.active) return reg;
    const coming = reg.installing || reg.waiting;
    if (!coming) return reg;
    await new Promise<void>(done => {
      const look = () => {
        if (coming.state === 'activated' || coming.state === 'redundant') { coming.removeEventListener('statechange', look); done(); }
      };
      coming.addEventListener('statechange', look);
      look();
      window.setTimeout(done, ACTIVE_WAIT);   // a slow install never leaves the button spinning
    });
    return reg;
  } catch { return null; }
}

// This device's subscription once it has one, so the heartbeat below needs no registration lookup of its own.
let endpointHeld = '';
let saidAt = 0;
let saidWhere = '';

/**
 * "This device has that chat open." Called by the open chat window; it writes nothing and asks nothing until this
 * device has actually allowed banners, and at most once every HERE_MS — a chat window asks the server every few
 * seconds and this must not ride along with it.
 */
export function pushHere(convId: string): void {
  if (!endpointHeld || !convId) return;
  const at = Date.now();
  if (convId === saidWhere && at - saidAt < HERE_MS) return;
  saidWhere = convId;
  saidAt = at;
  api.chatPushHere(endpointHeld, convId).catch(() => { /* the next tick says it again */ });
}

/** What the button beside the notices bell — on a phone, the banner over the chat list — shows and does. */
export type ChatPushButton = {
  show: boolean;            // banners are possible here at all — no button otherwise
  on: boolean | null;       // this device has allowed them; null = not yet known, so nothing is drawn
  busy: boolean;
  err: '' | 'blocked' | 'failed';
  allow: () => Promise<void>;
  stop: () => Promise<void>;
  homeScreen: boolean;      // an iPhone tab on a server that can send them: possible once Chat is on the Home Screen
  dismissed: boolean;       // the phone's banner was put away on this device
  dismiss: () => void;
};

export function useChatPush(): ChatPushButton {
  const [show, setShow] = useState(false);
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<'' | 'blocked' | 'failed'>('');
  const [homeScreen, setHomeScreen] = useState(false);
  const [dismissed, setDismissed] = useState(askPutAway);
  const key = useRef('');
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // What the server can do, and what this device has already said. Asked once with the window, beside the notices
  // switch: nothing is asked of the person here, and no dialog is opened — only a button is drawn or not.
  useEffect(() => {
    const iphone = pushNeedsHomeScreen();
    if (!pushSupported() && !iphone) { setShow(false); setOn(null); return; }
    let gone = false;
    (async () => {
      let state: { available?: boolean; key?: string } = {};
      try { state = await api.chatPush(); } catch { state = {}; }
      if (gone) return;
      if (!state.available || !state.key) { setShow(false); setOn(null); return; }
      // An iPhone tab: nothing can be allowed from here, so there is no button — only the words that say how, and
      // only because this server really can send one, or they would promise a banner that never comes.
      if (iphone) { setHomeScreen(true); return; }
      key.current = String(state.key);
      setShow(true);
      // Already allowed on this device? The browser itself is the record of that, not the server: a subscription
      // belongs to this browser and no other, so this is the one place that can say.
      let has = false;
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) { endpointHeld = sub.endpoint; has = true; }
      } catch { /* none */ }
      if (!gone) setOn(has && Notification.permission === 'granted');
    })();
    return () => { gone = true; };
  }, []);

  // The press. The browser's own "Allow notifications?" opens here and nowhere else, which is what keeps a refusal
  // from being spent before the person has been told what it is for.
  const allow = useCallback(async () => {
    setErr('');
    setBusy(true);
    try {
      const said = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (said !== 'granted') { if (alive.current) { setErr('blocked'); setOn(false); } return; }
      const reg = await chatWorker();
      if (!reg) { if (alive.current) setErr('failed'); return; }
      const sub = await reg.pushManager.getSubscription()
        || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key.current) });
      const out = sub.toJSON();
      const keys = out.keys || {};
      await api.chatPushOn(String(out.endpoint || sub.endpoint), String(keys.p256dh || ''), String(keys.auth || ''));
      endpointHeld = sub.endpoint;
      if (alive.current) setOn(true);
    } catch {
      // A browser that refuses outright (an iPhone that is not installed to the Home Screen says so here) and a
      // server that could not keep it both land here: the button goes back to off and says so, and the in-app
      // cards are untouched either way.
      if (alive.current) { setErr(Notification.permission === 'denied' ? 'blocked' : 'failed'); setOn(false); }
    } finally { if (alive.current) setBusy(false); }
  }, []);

  // Off again, on this device alone: the subscription is given up here and dropped at the server, so nothing is
  // posted to an endpoint that would never be read.
  const stop = useCallback(async () => {
    setErr('');
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      const endpoint = sub?.endpoint || endpointHeld;
      if (sub) await sub.unsubscribe().catch(() => { /* already gone */ });
      if (endpoint) await api.chatPushOff(endpoint);
      endpointHeld = '';
      if (alive.current) setOn(false);
    } catch { if (alive.current) setErr('failed'); }
    finally { if (alive.current) setBusy(false); }
  }, []);

  // The phone banner's ×. It asks the browser nothing and turns nothing off: it only stops offering.
  const dismiss = useCallback(() => {
    try { localStorage.setItem(ASK_KEY, 'off'); } catch { /* private mode: put away for this visit only */ }
    setDismissed(true);
  }, []);

  return { show, on, busy, err, allow, stop, homeScreen, dismissed, dismiss };
}
